import type { Brand } from './brand.js';

/**
 * The schema default, used here as a floor rather than as a default.
 *
 * A harvested spacing scale is a measurement of somebody else's layout, and the
 * measurement can be wrong in ways that only show up in ours — kino-vela was
 * published with `[0,1,2,3,4,6,8,12,16]`, which put 3px of padding inside every
 * card on the live tenant page. Half of each default step is the point below
 * which a step has stopped being spacing and become a rounding error.
 */
const DEFAULT_SPACE_SCALE = [0, 4, 8, 12, 16, 24, 32, 48, 64];

/** A spacing scale whose largest step is under this is not measured in pixels. */
const MIN_TOP_STEP = 24;

/** Below this, two type steps are the same size to the eye and the scale carries no hierarchy. */
const MIN_TYPE_RATIO = 1.125;

/**
 * A token name or value ends up inside a `<style>` element on our own origin,
 * and every one of them was copied out of a stranger's stylesheet.
 *
 * `ColorToken`'s regex anchors the START of a colour and says nothing about the
 * rest, so `rgba(0,0,0,1)</style><script>…` is a valid colour by the schema; a
 * font stack, an easing curve, a shadow and the KEYS of the type, tracking,
 * radius and elevation records are free strings with no check at all. The widget
 * filters `brand.customCss` for exactly this (`safeCustomCss` in
 * apps/server/src/ui/widget.ts) and the tokens went out beside it unfiltered —
 * the same hole, one line higher up. A declaration that could end the rule, the
 * comment or the element is dropped whole rather than escaped: a token nobody
 * can paint with falls through to the renderer's own literal, which is a
 * slightly wrong colour, while a token that closes the element is a script tag
 * on a page that carries the operator's name.
 */
const SAFE_TOKEN_NAME = /^[A-Za-z0-9_-]+$/;
const UNSAFE_TOKEN_VALUE = /[<>;{}@\\]|\/\*|\*\/|url\s*\(|expression\s*\(/i;

/** One `--ch-*` declaration, or nothing when either half could break out of it. */
function declaration(name: string, value: string): string | null {
  if (!SAFE_TOKEN_NAME.test(name) || UNSAFE_TOKEN_VALUE.test(value)) return null;
  return `  --ch-${name}: ${value};`;
}

/** Comment text that cannot end the comment it sits in. */
function commentSafe(text: string): string {
  return text.replace(/[<>]/g, ' ').replace(/\*\//g, '* /');
}

/** What the normaliser changed, in the operator's vocabulary. */
export interface TokenCorrection {
  /** `density.scale` or `typography.scale.steps` — the field, as the manifest names it. */
  field: string;
  was: string;
  now: string;
  why: string;
}

export interface NormalizedBrand {
  brand: Brand;
  corrections: TokenCorrection[];
}

/**
 * Spacing steps as pixels, in a range a layout survives.
 *
 * `spaceBase` is the site's own unit, and a scale whose top step is smaller than
 * a single comfortable gap is a scale expressed in those units rather than in
 * pixels — multiplying is reading it the way the site meant it, not inventing a
 * number. Floors and monotonicity come after, because a scale can be honestly in
 * pixels and still be too tight to put text in; and if even the floors leave the
 * scale unusable then it carried no information and the default is the honest
 * answer.
 */
function normalizeSpace(density: Brand['density'], into: TokenCorrection[]): number[] {
  const raw = density.scale.filter((v) => Number.isFinite(v) && v >= 0);
  const was = `[${density.scale.join(', ')}]`;
  if (raw.length === 0) {
    into.push({ field: 'density.scale', was, now: `[${DEFAULT_SPACE_SCALE.join(', ')}]`, why: 'no usable steps' });
    return [...DEFAULT_SPACE_SCALE];
  }

  const reasons: string[] = [];
  let scale = raw;
  const base = density.spaceBase;
  if (Math.max(...scale) < MIN_TOP_STEP && Number.isFinite(base) && base > 1) {
    scale = scale.map((v) => v * base);
    reasons.push(`read in units of spaceBase ${base}`);
  }

  let running = 0;
  const floored = scale.map((v, i) => {
    const floor = (DEFAULT_SPACE_SCALE[i] ?? DEFAULT_SPACE_SCALE[DEFAULT_SPACE_SCALE.length - 1]!) / 2;
    running = Math.max(running, v, floor);
    return running;
  });
  if (floored.some((v, i) => v !== scale[i])) reasons.push('steps raised to a usable minimum');

  if (Math.max(...floored) < MIN_TOP_STEP) {
    into.push({ field: 'density.scale', was, now: `[${DEFAULT_SPACE_SCALE.join(', ')}]`, why: 'no step large enough to lay out with' });
    return [...DEFAULT_SPACE_SCALE];
  }
  if (reasons.length) {
    into.push({ field: 'density.scale', was, now: `[${floored.join(', ')}]`, why: reasons.join('; ') });
  }
  return floored;
}

/**
 * Type steps far enough apart to read as a hierarchy.
 *
 * kino-vela harvested `md: 14` and `lg: 14.4`: a heading four tenths of a pixel
 * larger than its body text, which is not a heading. When any neighbouring pair
 * is that close the scale is regenerated from the tenant's own base size and its
 * own snapped ratio — the sizes stay the tenant's, only the intervals become
 * ones a reader can see. The step nearest the base size stays put, so the body
 * text does not change size under the operator.
 */
function normalizeTypeSteps(t: Brand['typography'], into: TokenCorrection[]): Record<string, number> {
  const entries = Object.entries(t.scale.steps)
    .filter(([, px]) => Number.isFinite(px) && px > 0)
    .sort((a, b) => a[1] - b[1]);
  if (entries.length < 2) return t.scale.steps;

  const tooClose = entries.some(([, px], i) => i > 0 && px / entries[i - 1]![1] < MIN_TYPE_RATIO);
  if (!tooClose) return t.scale.steps;

  const ratio = Math.max(t.scale.ratio, MIN_TYPE_RATIO);
  let baseIndex = 0;
  for (let i = 1; i < entries.length; i++) {
    if (Math.abs(entries[i]![1] - t.baseSizePx) < Math.abs(entries[baseIndex]![1] - t.baseSizePx)) baseIndex = i;
  }

  const steps: Record<string, number> = {};
  entries.forEach(([name], i) => {
    steps[name] = Math.round(t.baseSizePx * ratio ** (i - baseIndex) * 10) / 10;
  });
  into.push({
    field: 'typography.scale.steps',
    was: entries.map(([k, v]) => `${k} ${v}`).join(', '),
    now: entries.map(([k]) => `${k} ${steps[k]}`).join(', '),
    why: `two steps were closer than ${MIN_TYPE_RATIO}×; regenerated from ${t.baseSizePx}px × ${ratio}`,
  });
  return steps;
}

/**
 * A harvested brand, made safe to lay out with.
 *
 * Every consumer of `tokensToCss` gets this for free, and it is exported because
 * the surfaces that consume the brand OBJECT rather than the CSS string —
 * `baseStylesheet`'s literal fallbacks, the console's brand card — have to agree
 * with the emitted tokens or the fallback repaints the defect the tokens fixed.
 * Colour is deliberately not touched here: contrast repair needs the pairs the
 * stylesheet actually puts on screen, which is `contrastSafeOverrides`' job.
 *
 * Idempotent: a normalised brand normalises to itself, so it is safe to apply at
 * every layer that needs it rather than exactly once.
 */
export function normalizeBrandTokens(brand: Brand): NormalizedBrand {
  const corrections: TokenCorrection[] = [];
  const scale = normalizeSpace(brand.density, corrections);
  const steps = normalizeTypeSteps(brand.typography, corrections);
  if (corrections.length === 0) return { brand, corrections };
  return {
    brand: {
      ...brand,
      density: { ...brand.density, scale },
      typography: { ...brand.typography, scale: { ...brand.typography.scale, steps } },
    },
    corrections,
  };
}

/**
 * Brand → CSS custom properties.
 *
 * Both surfaces paint from the same string. The web surface inlines it in a
 * <style> tag; the MCP Apps widget inlines it too, but layers the host's own
 * theme variables on top afterwards (brand owns the accent, host owns the
 * ground) so a dark tenant palette doesn't punch a hole in a light chat client.
 *
 * The brand is normalised on the way in, and every correction is emitted as a
 * comment beside the tokens: an operator reading the source of their own tenant
 * page is entitled to see that we moved their scale and why.
 */
export function tokensToCss(raw: Brand, selector = ':root'): string {
  const normalized = normalizeBrandTokens(raw);
  const brand = normalized.brand;
  const lines: string[] = normalized.corrections.map(
    (c) => `  /* ${commentSafe(`${c.field} normalised: ${c.was} -> ${c.now} (${c.why})`)} */`,
  );
  const push = (name: string, value: string): void => {
    const line = declaration(name, value);
    if (line) lines.push(line);
  };

  for (const [role, token] of Object.entries(brand.palette)) {
    if (!token) continue;
    push(role.replace(/\./g, '-'), token.value);
  }
  // The role is `border.default`, but the MCP Apps widget asks for `--ch-border`
  // — a name this emitter never wrote, so every field and dashed rule in the
  // widget fell through to its grey literal and the tenant's own line colour was
  // never used. The alias makes the two spellings agree from the emitting side,
  // which is the side that can fix it for a widget already cached in somebody's
  // chat client.
  const borderValue = brand.palette['border.default']?.value;
  if (borderValue) push('border', borderValue);

  const t = brand.typography;
  push('font-sans', t.families.sans.stack);
  if (t.families.display) push('font-display', t.families.display.stack);
  if (t.families.mono) push('font-mono', t.families.mono.stack);
  push('font-size-base', `${t.baseSizePx}px`);
  push('font-weight-base', `${t.baseWeight}`);
  push('font-weight-heading', `${t.headingWeight}`);
  for (const [step, px] of Object.entries(t.scale.steps)) push(`size-${step}`, `${px}px`);
  push('case-heading', t.case.heading);
  push('case-button', t.case.button);
  for (const [k, v] of Object.entries(t.tracking)) push(`tracking-${k}`, v);

  for (const [k, v] of Object.entries(brand.shape.radius)) push(`radius-${k}`, `${v}px`);
  push('border-width', `${brand.shape.borderWidth}px`);
  for (const [k, v] of Object.entries(brand.elevation)) push(`shadow-${k}`, v);

  brand.density.scale.forEach((v, i) => push(`space-${i}`, `${v}px`));
  push('motion-duration', `${brand.motion.durationMs}ms`);
  push('motion-easing', brand.motion.easing);
  lines.push(`  color-scheme: ${brand.mode};`);

  return `${selector} {\n${lines.join('\n')}\n}`;
}

/**
 * Google Fonts <link> for the substituted family, or null when the tenant's
 * type is system-only. Only ever emitted for a `substitute`, never for the
 * observed face — we do not rehost harvested font binaries.
 */
export function googleFontHref(brand: Brand): string | null {
  // Mono is here because `--ch-font-mono` is emitted like the other two: leaving
  // it out of the link meant a tenant whose mono face was substituted served a
  // stack whose first family the browser never loaded.
  const fams = [brand.typography.families.sans, brand.typography.families.display, brand.typography.families.mono]
    .filter((f): f is NonNullable<typeof f> => f != null)
    .map((f) => f.substitute)
    .filter((s): s is NonNullable<typeof s> => s?.source === 'google');
  if (!fams.length) return null;

  const seen = new Set<string>();
  const specs: string[] = [];
  for (const s of fams) {
    if (seen.has(s.family)) continue;
    seen.add(s.family);
    const weights = [...new Set(s.weights)].sort((a, b) => a - b).join(';');
    // The family name is harvested too, and this string ends up in an href on a
    // page we serve: percent-encoding it keeps a name carrying a quote or an
    // ampersand from becoming a second attribute. Spaces stay `+` because that
    // is the form Google Fonts documents.
    specs.push(`family=${encodeURIComponent(s.family).replace(/%20/g, '+')}:wght@${weights}`);
  }
  return `https://fonts.googleapis.com/css2?${specs.join('&')}&display=swap`;
}
